import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  MeetingGuidanceBuilderQuestionDraft,
  MeetingGuidanceBuilderSectionDraft,
  MeetingGuidanceSignalExplanations,
} from '../types';

interface MeetingQuestionBuilderProps {
  questions: MeetingGuidanceBuilderQuestionDraft[];
  sections: MeetingGuidanceBuilderSectionDraft[];
  signalExplanations: MeetingGuidanceSignalExplanations | undefined;
  isMeetingReady: boolean;
  onQuestionChange: (
    question: MeetingGuidanceBuilderQuestionDraft,
    previousAgendaItemId?: string | null,
  ) => void;
  onQuestionRemove: (questionId: string) => void;
}

const formatLabel = (value: string | null | undefined) =>
  value
    ? value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : 'Unassigned';

const createQuestionId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `question-custom-${crypto.randomUUID()}`;
  }
  return `question-custom-${Date.now()}`;
};

export const MeetingQuestionBuilder = ({
  questions,
  sections,
  signalExplanations,
  isMeetingReady,
  onQuestionChange,
  onQuestionRemove,
}: MeetingQuestionBuilderProps) => {
  const [customQuestionText, setCustomQuestionText] = useState('');
  const [customSectionId, setCustomSectionId] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [customError, setCustomError] = useState('');

  const availableDomains = Array.from(
    new Set(
      questions
        .map((question) => question.domain)
        .filter((domain): domain is string => Boolean(domain)),
    ),
  );

  const sectionByAgendaId = new Map(
    sections
      .filter((section) => section.agenda_item_id)
      .map((section) => [section.agenda_item_id, section]),
  );

  const getEvidenceSummaries = (
    question: MeetingGuidanceBuilderQuestionDraft,
  ) => {
    const sourceSignalIds = new Set(question.source_signal_ids);
    return signalExplanations?.question_evidence
      .filter(
        (evidence) =>
          evidence.agenda_item_id === question.agenda_item_id ||
          evidence.domain === question.domain ||
          evidence.source_signal_ids.some((signalId) =>
            sourceSignalIds.has(signalId),
          ),
      )
      .map((evidence) => evidence.summary)
      .slice(0, 2);
  };

  const handleAddCustomQuestion = () => {
    const trimmedQuestion = customQuestionText.trim();
    if (!trimmedQuestion) {
      setCustomError('Enter a custom advisor question.');
      return;
    }
    if (!customSectionId) {
      setCustomError('Choose a section for the custom question.');
      return;
    }

    const question: MeetingGuidanceBuilderQuestionDraft = {
      question_id: createQuestionId(),
      source_question_ref: null,
      question_text: trimmedQuestion,
      edited_question_text: null,
      source: 'advisor_custom',
      priority: 'medium',
      domain: customDomain || null,
      topic_id: null,
      subtopic_id: null,
      agenda_item_id: customSectionId,
      source_signal_ids: [],
      is_selected: true,
      is_custom: true,
      audience: 'advisor_only',
    };
    onQuestionChange(question);
    setCustomQuestionText('');
    setCustomSectionId('');
    setCustomDomain('');
    setCustomError('');
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h5SMedium">Question Builder</Typography>
            <Typography variant="par02Regular" color="text.secondary">
              Select generated questions, edit advisor wording, and add custom
              advisor-only questions for the meeting.
            </Typography>
          </Stack>
          {!isMeetingReady && (
            <Alert severity="info">
              Generated recommendations unlock after intake is saved. You can
              still add custom advisor questions now.
            </Alert>
          )}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="par01SemiBold">
                  Add Custom Advisor Question
                </Typography>
                {customError && <Alert severity="warning">{customError}</Alert>}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Custom question"
                  helperText="Use open-ended behavioral-finance language."
                  value={customQuestionText}
                  onChange={(event) => setCustomQuestionText(event.target.value)}
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="custom-question-section-label">
                      Section
                    </InputLabel>
                    <Select
                      labelId="custom-question-section-label"
                      label="Section"
                      value={customSectionId}
                      onChange={(event) =>
                        setCustomSectionId(event.target.value)
                      }
                    >
                      {sections.map((section) => (
                        <MenuItem
                          key={section.section_id}
                          value={section.agenda_item_id ?? section.section_id}
                        >
                          {section.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="custom-question-domain-label">
                      Domain Optional
                    </InputLabel>
                    <Select
                      labelId="custom-question-domain-label"
                      label="Domain Optional"
                      value={customDomain}
                      onChange={(event) => setCustomDomain(event.target.value)}
                    >
                      <MenuItem value="">No domain</MenuItem>
                      {availableDomains.map((domain) => (
                        <MenuItem key={domain} value={domain}>
                          {formatLabel(domain)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleAddCustomQuestion}
                    sx={{ minWidth: 180 }}
                  >
                    Add Question
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
          {questions.length > 0 ? (
            <Stack spacing={2}>
              {questions.map((question) => {
                const evidenceSummaries = getEvidenceSummaries(question);
                const section = sectionByAgendaId.get(
                  question.agenda_item_id ?? '',
                );
                return (
                  <Card key={question.question_id} variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip
                            label={
                              question.is_custom
                                ? 'Custom'
                                : 'Generated recommendation'
                            }
                            color={question.is_custom ? 'default' : 'primary'}
                          />
                          <Chip label={`Section: ${section?.title ?? 'None'}`} />
                          {question.domain && (
                            <Chip label={`Domain: ${formatLabel(question.domain)}`} />
                          )}
                          {question.source_question_ref &&
                            !question.is_custom &&
                            question.edited_question_text && (
                              <Chip label="Edited" color="warning" />
                            )}
                        </Stack>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={question.is_selected}
                              onChange={(event) =>
                                onQuestionChange({
                                  ...question,
                                  is_selected: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Include in advisor meeting plan"
                        />
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Question text"
                          value={
                            question.edited_question_text ??
                            question.question_text
                          }
                          onChange={(event) =>
                            onQuestionChange({
                              ...question,
                              edited_question_text: event.target.value,
                            })
                          }
                        />
                        <FormControl fullWidth>
                          <InputLabel id={`${question.question_id}-section-label`}>
                            Section
                          </InputLabel>
                          <Select
                            labelId={`${question.question_id}-section-label`}
                            label="Section"
                            value={question.agenda_item_id ?? ''}
                            onChange={(event) =>
                              onQuestionChange(
                                {
                                  ...question,
                                  agenda_item_id: event.target.value || null,
                                },
                                question.agenda_item_id,
                              )
                            }
                          >
                            {sections.map((sectionOption) => (
                              <MenuItem
                                key={sectionOption.section_id}
                                value={
                                  sectionOption.agenda_item_id ??
                                  sectionOption.section_id
                                }
                              >
                                {sectionOption.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {evidenceSummaries && evidenceSummaries.length > 0 && (
                          <Box>
                            <Typography variant="par01SemiBold">
                              Supporting evidence
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                              {evidenceSummaries.map((summary) => (
                                <Typography
                                  key={summary}
                                  variant="par02Regular"
                                  color="text.secondary"
                                >
                                  {summary}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        )}
                        {question.source_question_ref &&
                          !question.is_custom &&
                          question.source_question_ref.includes('missing') && (
                            <Alert severity="warning">
                              Source guidance changed. Review whether to keep
                              this saved question.
                            </Alert>
                          )}
                        {question.is_custom && (
                          <Box>
                            <Button
                              variant="text"
                              color="error"
                              onClick={() => onQuestionRemove(question.question_id)}
                            >
                              Remove custom question
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="par02Regular" color="text.secondary">
              No questions have been added yet.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
